import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSWRConfig } from 'swr';
import useSWRInfinite, { type SWRInfiniteConfiguration } from 'swr/infinite';
import denApi from '@/lib/api/denApi';
import { useAuthClient } from '@/lib/authClient';
import {
  type PaginationMeta,
  type PaginationQuery,
  type WithPagination,
} from '@mls-wallet/shared-types';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Standard paginated list response returned by ui-web list endpoints.
 */
export type PaginatedResponse<T> = WithPagination<T>;

type PaginationKey = [string, Record<string, string | number | undefined>];

function dedupeItemsByKey<T>(items: T[], getItemKey: (item: T) => string): T[] {
  const seenKeys = new Set<string>();

  return items.filter((item) => {
    const itemKey = getItemKey(item);

    if (seenKeys.has(itemKey)) {
      return false;
    }

    seenKeys.add(itemKey);
    return true;
  });
}

/**
 * Configuration for a cursor-paginated GET endpoint that returns
 * `{ data, pagination }`.
 *
 * `enabled` allows callers to keep the hook mounted without fetching, which is
 * useful for tabbed UIs where only the active tab should load.
 */
type UsePaginationConfig<T> = {
  path: string;
  pagination?: PaginationQuery;
  enabled?: boolean;
  query?: Record<string, string | number | undefined>;
  getItemKey?: (item: T) => string;
  swr?: SWRInfiniteConfiguration<PaginatedResponse<T>>;
};

/**
 * Generic cursor-pagination hook for session-authenticated ui-web endpoints.
 *
 * Assumptions:
 * - the endpoint is a GET route
 * - the route accepts `cursor` and `limit`
 * - the response shape is `WithPagination<T>`
 *
 * Behavior:
 * - page 1 loads with the provided `limit` and optional starting `cursor`
 * - later pages always use the cursor from the previous response
 * - `loadMore()` appends another page
 * - `refetch()` resets to page 1 and revalidates
 * - `reset()` collapses the list back to its first-page state without forcing
 *   an immediate revalidation on its own
 */
export function usePagination<T>({
  path,
  pagination,
  enabled = true,
  query,
  getItemKey,
  swr,
}: UsePaginationConfig<T>) {
  const session = useAuthClient().session;
  const { cache, mutate: globalMutate } = useSWRConfig();
  const pageSize = pagination?.limit ?? DEFAULT_PAGE_SIZE;
  const resolvedGetItemKey =
    getItemKey ?? ((item: T) => (item as { id: string }).id);
  const firstPageKeySignature = useMemo(() => {
    if (!enabled || !session.data) {
      return null;
    }

    return JSON.stringify([
      path,
      {
        ...(query ?? {}),
        limit: pageSize,
        ...(pagination?.cursor ? { cursor: pagination.cursor } : {}),
      },
    ]);
  }, [enabled, pageSize, pagination?.cursor, path, query, session.data]);
  const previousFirstPageKeySignatureRef = useRef(firstPageKeySignature);

  const getKey = (
    pageIndex: number,
    previousPageData: PaginatedResponse<T> | null
  ): PaginationKey | null => {
    if (!enabled || !session.data) {
      return null;
    }

    if (previousPageData && !previousPageData.pagination.hasMore) {
      return null;
    }

    if (pageIndex === 0) {
      // The first page can accept an explicit cursor, which lets callers resume
      // from a known point while still using the same hook contract.
      return [
        path,
        {
          ...(query ?? {}),
          limit: pageSize,
          ...(pagination?.cursor ? { cursor: pagination.cursor } : {}),
        },
      ];
    }

    const nextCursor = previousPageData?.pagination.cursor;

    if (!nextCursor) {
      return null;
    }

    // Every subsequent page is keyed off the cursor returned by the previous
    // response so the caller never needs to manage cursors manually.
    return [
      path,
      {
        ...(query ?? {}),
        cursor: nextCursor,
        limit: pageSize,
      },
    ];
  };

  const fetcher = async ([endpoint, query]: PaginationKey): Promise<
    PaginatedResponse<T>
  > =>
    await denApi.get<
      PaginatedResponse<T>,
      Record<string, string | number | undefined>
    >(endpoint, query);

  const { data, error, size, setSize, isLoading, isValidating } =
    useSWRInfinite<PaginatedResponse<T>>(getKey, fetcher, {
      revalidateOnFocus: false,
      persistSize: true,
      // Polling should refresh the first page so newly created transactions
      // can appear at the top without forcing every loaded page to refetch.
      revalidateAll: false,
      revalidateFirstPage: true,
      ...(swr ?? {}),
    });

  useEffect(() => {
    // When the first-page query changes, collapse the paginated state back to
    // page one so search/filter UIs do not fetch stale extra pages.
    if (previousFirstPageKeySignatureRef.current === firstPageKeySignature) {
      return;
    }

    previousFirstPageKeySignatureRef.current = firstPageKeySignature;
    void setSize(1);
  }, [firstPageKeySignature, setSize]);

  const pages = data ?? [];
  const items = dedupeItemsByKey(
    pages.flatMap((page) => page.data),
    resolvedGetItemKey
  );
  const lastPage = pages.at(-1);
  const paginationMeta: PaginationMeta = lastPage?.pagination ?? {
    cursor: null,
    hasMore: false,
  };
  const hasMore = lastPage?.pagination.hasMore ?? false;
  const isLoadingMore = isValidating && size > pages.length;

  const loadMore = useCallback(async (): Promise<void> => {
    if (!session.data || !hasMore || isLoadingMore) {
      return;
    }

    await setSize((currentSize) => currentSize + 1);
  }, [hasMore, isLoadingMore, session.data, setSize]);

  const clearPaginationCache = useCallback(async (): Promise<void> => {
    await globalMutate(
      (key) => Array.isArray(key) && key[0] === path,
      undefined,
      {
        revalidate: false,
      }
    );

    for (const key of [...cache.keys()]) {
      if (Array.isArray(key) && key[0] === path) {
        cache.delete(key);
      }
    }
  }, [cache, globalMutate, path]);

  const refetch = useCallback(async (): Promise<void> => {
    // Clear this endpoint's paginated cache before reloading page one so SWR
    // does not reuse stale no-cursor data from an earlier visit.
    await clearPaginationCache();
    await setSize(1);
  }, [clearPaginationCache, setSize]);

  const reset = useCallback(async (): Promise<void> => {
    // Tab switches should restart from a fresh first page instead of keeping
    // old paginated cache entries alive for the same endpoint.
    await clearPaginationCache();
    await setSize(1);
  }, [clearPaginationCache, setSize]);

  return {
    data: items,
    pagination: paginationMeta,
    hasMore,
    isLoading: enabled && (session.isPending || isLoading),
    isLoadingMore,
    error,
    loadMore,
    refetch,
    reset,
  };
}
